import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import { pct, fmtDate } from "./format";

// Human-readable label for each range source the backend emits.
// Mirrors RANGE_LABEL_TEXT in CalibrationPanel.jsx (not exported there, so
// duplicated locally per that file's own established pattern).
const RANGE_LABEL_TEXT = {
  calibrated: "90% range",
  recent_errors: "range of recent errors",
  model: "model range",
  calibrating: "calibrating",
};

/**
 * NotesConfidenceBlock - forecast confidence / calibration footnote.
 *
 * GET /api/v1/financial-control/calibration-report (bare) -> { window_months,
 *   total: { conformal: {coverage,n,label}, projection_mape: {median,p90,n} } }
 * (mirrors CalibrationPanel.jsx's report shape) for confidence level + coverage,
 * plus GET /api/v1/financial-control/summary -> { summary: { as_of } } for
 * freshness. The backend has no explicit "assumptions" field, so this block's
 * assumptions line is a documented binding default: the calibration window
 * length + methodology label + (when present) the month-end projection error.
 */
const NotesConfidenceBlock = ({ title = "Confidence & assumptions", period }) => {
  const calibration = useBlockData("/api/v1/financial-control/calibration-report", {
    params: period ? { period } : undefined,
  });
  const summary = useBlockData("/api/v1/financial-control/summary", {
    params: period ? { period } : undefined,
  });

  const isLoading = calibration.isLoading || summary.isLoading;
  const error = calibration.error || summary.error || null;

  const report = calibration.data;
  const total = report?.total || {};
  const conformal = total.conformal || {};
  const mape = total.projection_mape || {};
  const windowMonths = report?.window_months || 6;
  const asOf = summary.data?.summary?.as_of;

  const confidenceLabel = RANGE_LABEL_TEXT[conformal.label] || conformal.label || "—";
  const noHistory = conformal.n === 0 || conformal.n == null;

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error}>
      <p className="a4-notes-freshness">As of {fmtDate(asOf)}</p>
      <p className="a4-notes-confidence">{confidenceLabel}</p>
      <p className="a4-notes-coverage">
        {noHistory ? "Not enough history yet" : `Coverage ${pct(conformal.coverage)} (n=${conformal.n})`}
      </p>
      <ul className="a4-notes-assumptions">
        <li>Based on the last {windowMonths} months of history.</li>
        {mape.n > 0 && (
          <li>
            Month-end projection error: median {pct(mape.median)}, p90 {pct(mape.p90)}.
          </li>
        )}
      </ul>
    </BaseBlock>
  );
};

NotesConfidenceBlock.propTypes = {
  title: PropTypes.string,
  period: PropTypes.string,
};

export default NotesConfidenceBlock;
